<template>
	<div
		class="g-block-image-trio"
		:class="{
			'g-block-image-trio--edit': editable,
			'is-active': editable && active,
			'g-block-image-trio--h5': h5,
		}"
		:style="gridStyle"
		@click="onWrapClick"
	>
		<div
			class="g-block-image-trio__main"
			:class="{ 'is-clickable': isItemClickable(items[0]) }"
			:style="cellStyle"
			@click="onItemClick(items[0], $event)"
		>
			<img v-if="getImageUrl(items[0])" :src="getImageUrl(items[0])" alt="" />
			<i v-else class="mbm-iconfont mbm-tupian"></i>
			<div
				v-if="items[0]?.title"
				class="g-block-image-trio__caption"
				:style="{ color: items[0].titleColor }"
			>{{ items[0].title }}</div>
		</div>

		<div class="g-block-image-trio__side">
			<div
				v-for="index in [1, 2]"
				:key="items[index]?.id || index"
				class="g-block-image-trio__side-item"
				:class="{ 'is-clickable': isItemClickable(items[index]) }"
				:style="cellStyle"
				@click="onItemClick(items[index], $event)"
			>
				<img v-if="getImageUrl(items[index])" :src="getImageUrl(items[index])" alt="" />
				<i v-else class="mbm-iconfont mbm-tupian"></i>
				<div
					v-if="items[index]?.title"
					class="g-block-image-trio__caption"
					:style="{ color: items[index].titleColor }"
				>{{ items[index].title }}</div>
			</div>
		</div>

		<ImageGalleryLightbox
			v-model:visible="lightboxVisible"
			:urls="lightboxUrls"
			:initial-index="lightboxIndex"
		/>
	</div>
</template>

<script>
	import ImageGalleryLightbox from '../../../components/ImageGalleryLightbox.vue';
	import { navigateJumpLink } from '../../../utils/jumpLink.js';
	import {
		getImageTrioBlockStyle,
		getImageTrioImageUrl,
		normalizeImageTrioData,
	} from '../../../utils/imageTrio.js';

	export default {
		name: 'GImageTrioShell',
		components: { ImageGalleryLightbox },
		props: {
			data: { type: Object, required: true },
			editable: { type: Boolean, default: false },
			active: { type: Boolean, default: false },
			h5: { type: Boolean, default: false },
		},
		emits: ['select'],
		data() {
			return {
				lightboxVisible: false,
				lightboxUrls: [],
				lightboxIndex: 0,
			};
		},
		computed: {
			normalizedData() {
				return normalizeImageTrioData(this.data);
			},
			items() {
				return this.normalizedData.items;
			},
			blockStyle() {
				return getImageTrioBlockStyle(this.data, { h5: this.h5 });
			},
			gridStyle() {
				return {
					gap: this.blockStyle.gap,
					'--trio-gap': this.blockStyle.gap,
				};
			},
			cellStyle() {
				return {
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			isPreviewable() {
				return this.h5 && !this.editable;
			},
		},
		methods: {
			getImageUrl(item) {
				return getImageTrioImageUrl(item?.image);
			},
			getGalleryUrls() {
				return this.items.map(item => this.getImageUrl(item)).filter(Boolean);
			},
			isItemClickable(item) {
				if (!this.isPreviewable || !item) return false;
				return !!item.jumpLink?.type || !!this.getImageUrl(item);
			},
			onWrapClick(e) {
				if (!this.editable) return;
				e?.stopPropagation?.();
				this.$emit('select');
			},
			onItemClick(item, e) {
				if (!this.isPreviewable || !item) return;
				e?.stopPropagation?.();
				if (item.jumpLink?.type) {
					navigateJumpLink(item.jumpLink, {
						apiRequest: this.$apiRequest,
					});
					return;
				}
				const urls = this.getGalleryUrls();
				if (!urls.length) return;
				const url = this.getImageUrl(item);
				const index = Math.max(0, urls.indexOf(url));
				this.lightboxUrls = urls;
				this.lightboxIndex = index;
				this.lightboxVisible = true;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-image-trio {
		display: flex;
		align-items: stretch;
		width: 100%;
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-image-trio__main,
	.g-block-image-trio__side-item {
		position: relative;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: 0;
		min-height: 0;

		&.is-clickable {
			cursor: pointer;
		}

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		i {
			font-size: 28px;
			color: #bfbfbf;
		}

		.g-block-image-trio--h5 & i {
			font-size: 0.56rem;
		}

		&::after {
			content: '';
			position: absolute;
			left: 0;
			right: 0;
			bottom: 0;
			height: 42%;
			background: linear-gradient(transparent, rgba(0, 0, 0, 0.4));
			pointer-events: none;
		}
	}

	.g-block-image-trio__main {
		flex: 1.05 1 0;
		aspect-ratio: 1 / 1;

		i {
			font-size: 36px;
		}

		.g-block-image-trio--h5 & i {
			font-size: 0.72rem;
		}
	}

	.g-block-image-trio__side {
		flex: 1 1 0;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: var(--trio-gap, 8px);
	}

	.g-block-image-trio__side-item {
		flex: 1 1 0;
	}

	.g-block-image-trio__caption {
		position: absolute;
		left: 10px;
		right: 10px;
		bottom: 10px;
		z-index: 1;
		font-size: 13px;
		font-weight: 500;
		line-height: 1.3;
		color: #fff;
		text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
		word-break: break-word;

		.g-block-image-trio--h5 & {
			left: 0.2rem;
			right: 0.2rem;
			bottom: 0.2rem;
			font-size: 0.26rem;
		}

		.g-block-image-trio__main & {
			font-size: 15px;

			.g-block-image-trio--h5 & {
				font-size: 0.3rem;
			}
		}
	}
</style>
